import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { addFlashMessages } from '../../actions/FlashMessagesActions';
import { localeData } from '../../locales/data';
import { addModifiers, getLanguageCode } from '../../shared/helpers';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { DispatchConnect, IntlMessagesError } from '../../types/index';

import './Pagination.scss';

export interface PaginationPageSizeControlProps {
  pageSize: number;
  itemsCount: number;
  itemCountLimit?: number;
  changePageSize?: (value: number) => void;
  pageSizeLimit: number;
  allowPageSizeChange: boolean;
  cssModifiers?: [string];
  additionalMessage?: string;
}

interface State {
  pageSizeInputValue: string;
}

interface Dispatchers {
  dispatchFlashMessage: (errors: IntlMessagesError[]) => void;
}

type Props = PaginationPageSizeControlProps & Dispatchers;

export class PaginationPageSizeControl extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      pageSizeInputValue: this.props.pageSize.toString(),
    };
  }

  handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = parseInt(event.currentTarget.value, 10);
    this.changePageSizeIfValid(value);
  };

  handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const value = parseInt(event.currentTarget.value, 10);
      this.changePageSizeIfValid(value);
    }
  };

  handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ pageSizeInputValue: event.currentTarget.value });
  };

  /**
   * Only trigger a changePageSize callback when we have a valid value, otherwise reset to current value
   */
  changePageSizeIfValid = (newPageSize: number) => {
    if (this.props.changePageSize === undefined) {
      return;
    }
    if (newPageSize === this.props.pageSize) {
      return;
    } else if (!isNaN(newPageSize) && newPageSize > 0 && newPageSize <= this.props.pageSizeLimit) {
      this.props.changePageSize(newPageSize);
    } else {
      this.setState({ pageSizeInputValue: this.props.pageSize.toString() });
      this.props.dispatchFlashMessage([
        {
          msg: 'alphas.table.error',
          defaultMessage: localeData.en['alphas.table.error'],
          intlValues: { count: this.props.pageSizeLimit },
        },
      ]);
    }
  };

  componentDidUpdate(prevProps: PaginationPageSizeControlProps) {
    // Update our display value if some other component caused pageSize to change.
    const displayedSize = parseInt(this.state.pageSizeInputValue, 10);
    if (prevProps.pageSize !== this.props.pageSize && this.props.pageSize !== displayedSize) {
      this.setState({ pageSizeInputValue: this.props.pageSize.toString() });
    }
  }

  render() {
    const prefix = this.props.allowPageSizeChange ? 'tablePagination.show' : 'tablePagination.showing';

    return (
      <div className={addModifiers('pagination-page-size-control', this.props.cssModifiers)}>
        <FormattedMessageHelper id={prefix} />
        {this.props.allowPageSizeChange ? (
          <input
            type="number"
            name="pageSize"
            onBlur={this.handleBlur}
            onKeyPress={this.handleKeyPress}
            onChange={this.handleOnChange}
            className="pagination-page-size-control-num"
            value={this.state.pageSizeInputValue}
            min={1}
            max={this.props.pageSizeLimit}
          />
        ) : (
          <span>&nbsp;{this.state.pageSizeInputValue}&nbsp;</span>
        )}
        <FormattedMessageHelper id={'tablePagination.outOf'} />
        &nbsp;{this.formatItemCount()}
        {this.props.additionalMessage && <>.&nbsp;{this.props.additionalMessage}.</>}
      </div>
    );
  }

  private formatItemCount() {
    const overLimitMark = this.props.itemCountLimit === this.props.itemsCount ? '+' : '';
    return this.props.itemsCount.toLocaleString(getLanguageCode()) + overLimitMark;
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): Dispatchers => ({
  dispatchFlashMessage: (errors: IntlMessagesError[]) => dispatch(addFlashMessages(errors)),
});

export default connect<PaginationPageSizeControlProps, DispatchConnect<Dispatchers>, null>(
  null,
  mapDispatchToProps
)(PaginationPageSizeControl);
